import type {
    ShiftLedgerSummary,
    ShiftRegisterFilters,
    ShiftRegisterRow,
    ShiftStatus,
} from '@/api/models';
import { formatGel } from '@/utils/money';

/**
 * Pure helpers and presentational config for the shift register. No React, no
 * queries — the row models the table renders, the filter state the hook owns,
 * and the two derivations worth naming.
 */

export type ShiftStatusFilter = 'ALL' | ShiftStatus;

export type ShiftFilterState = {
    branchId: string;
    from: string;
    to: string;
    status: ShiftStatusFilter;
    page: number;
};

export const SHIFTS_PAGE_SIZE = 20;

/** Sentinel for "no branch filter" — a select cannot hold an empty key. */
export const ALL_BRANCHES = 'ALL';

/**
 * Opens on the last 30 days rather than on everything.
 *
 * An accountant reviewing shifts is nearly always looking at the recent past,
 * and an unbounded default would page through a year of them to reach
 * yesterday. Widening is one date change; narrowing from everything is not
 * obviously necessary until you have already scrolled.
 */
export const initialShiftFilters = (today: Date): ShiftFilterState => {
    const from = new Date(today);
    from.setDate(from.getDate() - 30);
    return {
        branchId: ALL_BRANCHES,
        from: toIso(from),
        to: toIso(today),
        status: 'ALL',
        page: 1,
    };
};

const pad = (value: number): string => value.toString().padStart(2, '0');

const toIso = (date: Date): string =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Filter state → the request. The `ALL` sentinels become absent fields. */
export const toShiftRegisterFilters = (
    filters: ShiftFilterState,
): ShiftRegisterFilters => ({
    ...(filters.branchId !== ALL_BRANCHES
        ? { branchId: filters.branchId }
        : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
    ...(filters.status !== 'ALL' ? { status: filters.status } : {}),
    page: filters.page,
    pageSize: SHIFTS_PAGE_SIZE,
});

export const SHIFT_STATUS_CHIP: Record<
    ShiftStatus,
    { labelKey: string; className: string }
> = {
    Closed: {
        className: 'bg-default/40 text-muted',
        labelKey: 'branches.shifts.status.closed',
    },
    Open: {
        className: 'bg-success/10 text-success-soft-foreground',
        labelKey: 'branches.shifts.status.open',
    },
};

/**
 * How a close discrepancy should read.
 *
 * Three states, not two: a shift that has not closed has **no** discrepancy,
 * which is a different fact from one that closed exactly on the count. Rendering
 * both as `₾ 0.00` would claim an open drawer had already been reconciled.
 */
export type DiscrepancyTone = 'none' | 'balanced' | 'over' | 'short';

export const discrepancyTone = (
    closeDiscrepancyTetri: number | null,
): DiscrepancyTone => {
    if (closeDiscrepancyTetri === null) {
        return 'none';
    }
    if (closeDiscrepancyTetri === 0) {
        return 'balanced';
    }
    return closeDiscrepancyTetri > 0 ? 'over' : 'short';
};

export const DISCREPANCY_CLASS: Record<DiscrepancyTone, string> = {
    balanced: 'text-muted',
    none: 'text-muted',
    over: 'text-warning',
    short: 'text-danger',
};

/** The dash every "there is no figure here" cell uses. */
export const NO_FIGURE = '–';

export const formatTetriOrDash = (tetri: number | null): string =>
    tetri === null ? NO_FIGURE : formatGel(tetri);

/**
 * Whether the rollup's two sides agree.
 *
 * They always should — every entry it sums was balanced, so their sum is — and
 * that is exactly why it is worth checking rather than asserting. A rollup that
 * silently did not balance would be a reporting error wearing a totals row.
 */
export const rollupBalances = (summary: ShiftLedgerSummary): boolean =>
    summary.totalDebitTetri === summary.totalCreditTetri;

/**
 * Receipts the rollup left out, for the line under the table. Zero means the
 * table is the whole shift, and the line says so rather than disappearing —
 * "nothing was excluded" is information, and its absence reads as an oversight.
 */
export const excludedCount = (summary: ShiftLedgerSummary): number =>
    summary.receiptsReversed + summary.receiptsUnposted;

/** True when the shift took nothing at all — the empty state, not a table of zeros. */
export const isEmptyRollup = (summary: ShiftLedgerSummary): boolean =>
    summary.accounts.length === 0;

/**
 * One option in the branch filter (BOS-167).
 *
 * `archived` is carried rather than filtered on: archiving pulls a branch out
 * of the pickers you *choose* with — you cannot sell there any more — and must
 * not pull it out of a filter over history. An archived Batumi's two shifts sat
 * in the register with no way to isolate them. The chip says which is which.
 */
export type BranchFilterOption = {
    id: string;
    name: string;
    archived: boolean;
};

/** Hours after which an open shift is worth pointing at. */
export const STALE_SHIFT_HOURS = 24;

/**
 * Whether a shift has been open long enough to be a problem (BOS-167).
 *
 * A drawer is opened at the start of a working day and counted down at the end
 * of it. One still open a day later is not a busy shift — it is a shift nobody
 * closed, and every sale since has been attributed to it. The audit found one
 * open for eleven days with nothing on any screen pointing at it.
 *
 * Closed shifts are never stale, whatever their length: the count happened.
 */
export const isStaleShift = (
    row: Pick<ShiftRegisterRow, 'status' | 'openedAt'>,
    now: Date,
): boolean => {
    if (row.status !== 'Open') {
        return false;
    }
    const openedAt = new Date(row.openedAt).getTime();
    if (Number.isNaN(openedAt)) {
        return false;
    }
    return now.getTime() - openedAt >= STALE_SHIFT_HOURS * 60 * 60 * 1000;
};

/** Whole days a shift has been open, for the warning chip's label. */
export const openForDays = (
    row: Pick<ShiftRegisterRow, 'openedAt'>,
    now: Date,
): number => {
    const openedAt = new Date(row.openedAt).getTime();
    if (Number.isNaN(openedAt)) {
        return 0;
    }
    return Math.max(
        0,
        Math.floor((now.getTime() - openedAt) / (24 * 60 * 60 * 1000)),
    );
};

/** The register row, reduced to what the table renders. */
export type ShiftRowModel = ShiftRegisterRow & {
    discrepancyTone: DiscrepancyTone;
    /** Open longer than a day — see {@link isStaleShift}. */
    isStale: boolean;
    /** Whole days open, for the chip's label. Only meaningful when stale. */
    openForDays: number;
};

/**
 * Whether the drawer and the books agree, for the page's reconciliation banner
 * (BOS-164) — the same claim the fixed-asset and payroll registers make about
 * themselves.
 *
 * A closed shift reconciles when its difference was **booked**: a nil difference
 * needed no entry, and a non-nil one has `closeJournalEntryId`. A closed shift
 * with a difference and no entry is a shift closed before bos-api posted them at
 * all, which is most of the history — reported as `legacy` rather than as a
 * failure, because nothing is wrong with the shift; the ledger simply never
 * heard about it.
 */
export type ShiftReconciliation = {
    /** Closed shifts on this page. */
    closed: number;
    /** Of those, how many had a difference that reached the ledger. */
    booked: number;
    /** Of those, how many carry a difference the ledger never heard about. */
    legacy: number;
    /** Σ of every booked difference, tetri — what the ledger absorbed. */
    bookedTetri: number;
    inAgreement: boolean;
};

export const toShiftReconciliation = (
    rows: ShiftRegisterRow[],
): ShiftReconciliation => {
    const closed = rows.filter((row) => row.closeDiscrepancyTetri !== null);
    const withDifference = closed.filter(
        (row) => row.closeDiscrepancyTetri !== 0,
    );
    const booked = withDifference.filter((row) => row.closeJournalEntryId);
    const legacy = withDifference.filter((row) => !row.closeJournalEntryId);

    return {
        closed: closed.length,
        booked: booked.length,
        legacy: legacy.length,
        bookedTetri: booked.reduce(
            (sum, row) => sum + (row.closeDiscrepancyTetri ?? 0),
            0,
        ),
        // Nothing unaccounted for on this page. A page of shifts that all
        // balanced is in agreement too — there was nothing to book.
        inAgreement: legacy.length === 0,
    };
};

export const toShiftRowModels = (
    rows: ShiftRegisterRow[],
    now: Date = new Date(),
): ShiftRowModel[] =>
    rows.map((row) => ({
        ...row,
        discrepancyTone: discrepancyTone(row.closeDiscrepancyTetri),
        isStale: isStaleShift(row, now),
        openForDays: openForDays(row, now),
    }));

/** How many rows on this page have been open longer than a day. */
export const staleShiftCount = (rows: ShiftRowModel[]): number =>
    rows.filter((row) => row.isStale).length;
